/**
 * 睡眠中の SpO2・呼吸の乱れ・呼吸数・HRV の推移（指標ごとに 1 つずつ）と、そこから読み取れること
 */

import { ArrowRight } from 'lucide-react'
import { useMemo } from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { SleepInsight, SleepSeries, SleepSeriesPoint } from '@/api/types/sleep'
import { Spinner } from '@/components/ui/spinner'
import { parseUtcDate } from '@/lib/date-utils'

import { formatJstTime } from '../format'
import { ToneIcon } from './ai-report-card'

type Props = {
  series: SleepSeries
  /** グラフから読み取れること（バックエンドで作る） */
  insights: SleepInsight[]
  /** 読み取れたことに対する AI の対策。まだ作っていなければ null */
  actions: string[] | null
  isAdvising: boolean
  /** 塗りつぶす睡眠の時間帯（ISO） */
  ranges: { start: string; end: string }[]
}

type Metric = {
  label: string
  unit: string
  color: string
  points: SleepSeriesPoint[]
  /** Y 軸の範囲。SpO2 は 100% が上限 */
  domain: [number | string, number | string]
  /** 目安の横線 */
  reference?: { y: number; label: string }
  summarize?: (points: Point[]) => string
}

type Point = { t: number; v: number }

/** 通知のヒントで「SpO2 が下がった」とみなす値（バックエンドの api/services/sleep/context.py の LOW_SPO2） */
const LOW_SPO2 = 88

const formatTick = (ms: number) => formatJstTime(new Date(ms).toISOString())

const summarizeValues = (points: Point[], unit: string): string => {
  const values = points.map((p) => p.v)
  const avg = values.reduce((sum, v) => sum + v, 0) / values.length
  return `平均 ${avg.toFixed(0)}${unit}・最低 ${Math.min(...values).toFixed(0)}${unit}・最高 ${Math.max(...values).toFixed(0)}${unit}`
}

/** 呼吸の乱れは区間の両端の点が 2 つずつ並ぶので、レベル 1 以上の区間の長さを足す */
const summarizeDisruption = (points: Point[]): string => {
  let ms = 0
  for (let i = 0; i + 1 < points.length; i += 2) {
    const [start, end] = points.slice(i, i + 2)
    if (start.v > 0) ms += end.t - start.t
  }
  return `乱れあり 計${Math.round(ms / 60_000)}分・最大レベル ${Math.max(...points.map((p) => p.v))}`
}

const MetricChart = ({ metric, areas }: { metric: Metric; areas: { x1: number; x2: number }[] }) => {
  const data: Point[] = useMemo(
    () => metric.points.map((p) => ({ t: parseUtcDate(p.t).getTime(), v: p.v })),
    [metric.points],
  )

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-2">
        <span className="flex items-center gap-1 text-xs font-medium">
          <span className="inline-block size-2 rounded-full" style={{ backgroundColor: metric.color }} />
          {metric.label}
        </span>
        <span className="text-[11px] text-muted-foreground tabular-nums">
          {metric.summarize ? metric.summarize(data) : summarizeValues(data, metric.unit)}
        </span>
      </div>
      <ResponsiveContainer width="100%" height={120}>
        <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          {areas.map((a) => (
            <ReferenceArea key={a.x1} x1={a.x1} x2={a.x2} fill="hsl(239 84% 67%)" fillOpacity={0.08} />
          ))}
          <XAxis
            dataKey="t"
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={formatTick}
            tick={{ fontSize: 10 }}
          />
          <YAxis domain={metric.domain} tick={{ fontSize: 10 }} allowDecimals={false} />
          {metric.reference && (
            <ReferenceLine
              y={metric.reference.y}
              stroke="hsl(0 75% 55%)"
              strokeDasharray="4 4"
              ifOverflow="extendDomain"
              label={{
                value: metric.reference.label,
                position: 'insideBottomRight',
                fontSize: 10,
                fill: 'hsl(0 75% 55%)',
              }}
            />
          )}
          <Tooltip
            labelFormatter={(ms) => formatTick(Number(ms))}
            formatter={(v) => [`${Number(v).toFixed(0)}${metric.unit}`, metric.label]}
            contentStyle={{ backgroundColor: 'var(--popover)', border: '1px solid var(--border)', fontSize: 12 }}
          />
          <Line dataKey="v" stroke={metric.color} dot={false} strokeWidth={1.5} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

const Insights = ({ insights }: { insights: SleepInsight[] }) => (
  <div className="space-y-1.5">
    <p className="text-xs font-medium">この夜のグラフからわかること</p>
    <ul className="space-y-1.5">
      {insights.map((insight) => (
        <li key={insight.text} className="flex gap-2 text-sm">
          <ToneIcon tone={insight.tone} />
          <span>{insight.text}</span>
        </li>
      ))}
    </ul>
  </div>
)

const Actions = ({ actions, isAdvising }: { actions: string[] | null; isAdvising: boolean }) => {
  if (isAdvising)
    return (
      <p className="flex items-center gap-2 text-xs text-muted-foreground">
        <Spinner className="size-3.5" />
        AI が対策を考えています…
      </p>
    )
  if (!actions?.length) return null
  return (
    <div className="space-y-1.5 rounded-lg bg-indigo-500/5 p-3">
      <p className="text-xs font-medium">どうすればいいか</p>
      <ul className="space-y-1.5">
        {actions.map((action) => (
          <li key={action} className="flex gap-2 text-sm">
            <ArrowRight className="mt-0.5 size-4 shrink-0 text-indigo-500" />
            <span>{action}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const BreathingChart = ({ series, ranges, insights, actions, isAdvising }: Props) => {
  const all: Metric[] = [
    {
      label: 'SpO2',
      unit: '%',
      color: 'hsl(199 89% 48%)',
      points: series.spo2,
      domain: ['dataMin - 2', 100],
      reference: { y: LOW_SPO2, label: `${LOW_SPO2}%` },
    },
    {
      label: '呼吸の乱れ',
      unit: '',
      color: 'hsl(25 95% 53%)',
      points: series.breathing_disruption,
      domain: [0, 'dataMax'],
      summarize: summarizeDisruption,
    },
    {
      label: '呼吸数',
      unit: '回/分',
      color: 'hsl(262 83% 58%)',
      points: series.respiration,
      domain: ['dataMin - 2', 'dataMax + 2'],
    },
    {
      label: 'HRV',
      unit: 'ms',
      color: 'hsl(330 81% 60%)',
      points: series.hrv,
      domain: ['dataMin - 5', 'dataMax + 5'],
    },
  ]
  const metrics = all.filter((m) => m.points.length > 0)
  if (metrics.length === 0) return null

  const areas = ranges.map((r) => ({ x1: parseUtcDate(r.start).getTime(), x2: parseUtcDate(r.end).getTime() }))

  return (
    <section className="space-y-3 rounded-xl border bg-card p-4 shadow-sm">
      <span className="text-sm font-medium">SpO2・呼吸・HRV</span>
      {insights.length > 0 && <Insights insights={insights} />}
      <Actions actions={actions} isAdvising={isAdvising} />
      {metrics.map((metric) => (
        <MetricChart key={metric.label} metric={metric} areas={areas} />
      ))}
      <p className="text-[11px] text-muted-foreground">
        薄い紫の塗りつぶしは眠っていた時間帯。Garmin は医療機器ではないので、診断には使えません。SpO2
        の低下や呼吸の乱れが何日も続くときは、受診を検討してください
      </p>
    </section>
  )
}
